type Point = [number, number];

const addPoints = (
  [pointA1, pointA2]: Point,
  [pointB1, pointB2]: Point,
): Point => [pointA1 + pointB1, pointA2 + pointB2];

const degreesToRadians = (degrees: number): number => degrees * (Math.PI / 180);

/**
 * The delta x,y to draw one side of the arrow after it has been rotated
 * @param length The length of one side of the arrow
 * @param rotation The rotation angle (in degrees) of the arrow so that it points on the circle
 * (0 degrees is at 3 o'clock)
 */
const getArrowMidpointDeltas = (length: number, rotation: number) => {
  const rotationAngle = degreesToRadians(rotation);

  // If the arrow were not rotated, side A would be horizontal (0 degrees / 3
  // o'clock) and side B would be vertical (90 degrees / 12 o'clock). So if we
  // rotate the arrow `rotation` degrees clockwise, side A is now `rotation`
  // degrees below 0 degrees. If we draw a straight line from the end of side A
  // back to 0 degrees, we've formed a right-angle triangle where that straight
  // line is `deltaY`, a line from the other end of side A to the straight line
  // is `deltaX`. Side A is the hypotenuse of the triangle. Given that we know
  // the rotation angle `rotation` and the length of the hypotenuse (`length`),
  // we can figure out `deltaX` and `deltaY` using sine & cosine. See:
  // https://revisionmaths.com/gcse-maths-revision/trigonometry/sin-cos-and-tan
  const deltaX = length * Math.cos(rotationAngle);
  const deltaY = length * Math.sin(rotationAngle);

  return { deltaX, deltaY };
};

interface GetCircularArcParams {
  /**
   * The length of one side of the arrow, when a reload spinner
   */
  arrowLength?: number;

  /**
   * The rotation angle (in degrees) of the arrow so that it points on the circle
   */
  arrowRotation?: number;

  /**
   * Diameter of the circle
   */
  diameter: number;

  /**
   * The angle (in degrees) at which the arc begins.
   * (0 degrees is at 3 o'clock)
   */
  startAngle: number;

  /**
   * The width/thickness of the circle stroke. The thickness increases inward so
   * that the circle remains the same size with changing thicknesses
   */
  strokeWidth: number;

  /**
   * How many degrees of circular arc to display (must be positive).
   * (A semicircle is 180 degrees)
   */
  sweep: number;
}

/**
 * Generates the SVG path of the specified circular arc for the loading spinner
 */
export const getCircularArcPath = ({
  arrowLength = 0,
  arrowRotation = 0,
  diameter,
  startAngle: startAngleDegrees,
  strokeWidth,
  sweep: sweepDegrees,
}: GetCircularArcParams): string => {
  // A simplified adaptation of: http://xahlee.info/js/svg_circle_arc.html

  const desiredRadius = diameter / 2;

  const { deltaX: arrowDeltaX } = getArrowMidpointDeltas(
    arrowLength,
    arrowRotation,
  );

  // the actual arc radius has to take into account the stroke width because SVG
  // adds the thickness to the *outside* of the radius. so by reducing the
  // radius the outer edge of the arc is our desired radius. and then if we're
  // displaying an arrow we have to push the circle in further so that the arrow
  // head isn't clipped
  const actualRadius = desiredRadius - strokeWidth / 2 - arrowDeltaX;

  const center: Point = [desiredRadius, desiredRadius];
  const startAngleRadians = degreesToRadians(startAngleDegrees);
  const sweepRadians = degreesToRadians(sweepDegrees);
  const endAngleRadians = startAngleRadians + sweepRadians;

  const [startX, startY] = addPoints(
    [
      actualRadius * Math.cos(startAngleRadians),
      actualRadius * Math.sin(startAngleRadians),
    ],
    center,
  );
  const [endX, endY] = addPoints(
    [
      actualRadius * Math.cos(endAngleRadians),
      actualRadius * Math.sin(endAngleRadians),
    ],
    center,
  );
  const largeArcFlag = sweepRadians > Math.PI ? 1 : 0;
  const sweepFlag = sweepRadians > 0 ? 1 : 0;

  return [
    'M', // move to (absolute position)
    startX.toFixed(2),
    startY.toFixed(2),
    'A', // elliptical arc (absolute position)
    actualRadius.toFixed(2),
    actualRadius.toFixed(2),
    0, // rotation
    largeArcFlag,
    sweepFlag,
    endX.toFixed(2),
    endY.toFixed(2),
  ].join(' ');
};

interface GetReloadArrowParams {
  /**
   * Diameter of the loading circle
   */
  diameter: number;

  /**
   * The length of one side of the arrow
   */
  length: number;

  /**
   * The rotation angle (in degrees) of the arrow so that it points on the circle
   * (0 degrees is at 3 o'clock)
   */
  rotation: number;

  /**
   * The width/thickness of the arrow & circle stroke.
   */
  strokeWidth: number;
}

/**
 * Generates the SVG path of the arrow for a "reload" loading spinner
 */
export const getReloadArrowPath = ({
  diameter,
  length,
  rotation,
  strokeWidth,
}: GetReloadArrowParams): string => {
  // The arrow is basically an "L" of equal sides rotated so that it points on
  // the circle. So we get back the deltas in the x,y to draw one side of the
  // arrow and use that to draw two lines: left/bottom to midpoint/top &
  // midpoint/top to right/bottom
  const { deltaX, deltaY } = getArrowMidpointDeltas(length, rotation);

  // start right at the edge (at 9 o'clock). The `startX` is pushed in half of
  // the stroke width since the stroke thickness is drawn on either side of the
  // line. The `startY` is half way down the circle + additional `deltaY` so
  // that we can draw upwards then downwards to draw one contiguous path.
  const startX = strokeWidth / 2;
  const startY = diameter / 2 + deltaY;

  return [
    'M', // move to (absolute position)
    startX.toFixed(2),
    startY.toFixed(2),
    'l', // line to (relative)
    deltaX.toFixed(2),
    (-deltaY).toFixed(2),
    'l', // line to (relative)
    deltaY.toFixed(2),
    deltaX.toFixed(2),
  ].join(' ');
};

/**
 * Generates the SVG path information for the check mark for a "process" loading spinner
 *
 * @param diameter Diameter of the loading circle
 */
export const getProcessCheckPathInfo = (diameter: number) => {
  // These are hardcoded percentages based on the sizes of the checkmarks in the
  // Figma mocks. Adjusting them tweaks the shape and size of the checkmark.
  const shortDeltaX = 0.1354 * diameter;
  const shortDeltaY = 0.1719 * diameter;
  const longDeltaX = 0.2396 * diameter;
  const longDeltaY = 0.3125 * diameter;

  const checkWidth = shortDeltaX + longDeltaX;
  const checkHeight = longDeltaY;

  // `startX` begins on the short side of the checkmark such that the whole
  // checkmark will be horizontally centered
  const startX = (diameter - checkWidth) / 2;
  // `startY` begins on the short side of the checkmark such that the whole
  // checkmark will be vertically centered, but since the short side is shorter
  // we start lower in the bounding box equal to the difference between where
  // the long side ends and the short side begins
  const startY = (diameter - checkHeight) / 2 + (longDeltaY - shortDeltaY);

  const path = [
    'M', // move to (absolute position)
    startX.toFixed(2),
    startY.toFixed(2),
    'l', // line to (relative)
    shortDeltaX.toFixed(2),
    shortDeltaY.toFixed(2),
    'l', // line to (relative)
    longDeltaX.toFixed(2),
    -longDeltaY.toFixed(2),
  ].join(' ');

  // the 2 sets of deltas make right triangles whose hypotenuses are the short
  // and long parts of the checkmark. we can use the Pythagorean theorem to find
  // the lengths
  const shortLength = Math.sqrt(shortDeltaX ** 2 + shortDeltaY ** 2);
  const longLength = Math.sqrt(longDeltaX ** 2 + longDeltaY ** 2);

  // in order to animate the check by animating the `stroke-dashoffset`, we need to know the length of the entire check path. The 0.1 is a fudge factor because of the rounding that `.toFixed()` introduces in the `path`
  const pathLength = (shortLength + longLength + 0.1).toFixed(2);

  return {
    d: path,
    strokeDasharray: pathLength,
    strokeDashoffset: pathLength,
  };
};
